/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { ReactNode, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Modal, ModalProps, Stack, Text } from '@mantine/core'
import ButtonGroup from '../form/ButtonGroup'

export type DeleteModalProps = ModalProps & {
  label?: string
  onConfirm (): Promise<unknown>
  onSuccess? (): void
  successMessage?: ReactNode
}

function DeleteModal (props: DeleteModalProps) {
  const {
    opened,
    label,
    onClose,
    onConfirm,
    onSuccess,
    successMessage,
    ...others
  } = props

  const mutation = useMutation({
    mutationFn: async () => onConfirm(),
    onSuccess
  })
  const { reset } = mutation

  useEffect(() => {
    if (opened) {
      reset()
    }
  }, [opened, reset])

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title={<FormattedMessage
        id="delete"
        defaultMessage="Supprimer"
      />}
      {...others}
    >
      <Stack>
        {mutation.isSuccess
          ? <Text>
            <FormattedMessage
              id="delete_succeeded"
              defaultMessage="La suppression a été effectuée."
            />
            {successMessage}
          </Text>
          : null}

        {!mutation.isSuccess
          ? <Text>
            {label != null
              ? <FormattedMessage
                id="do_you_want_to_delete_%item"
                defaultMessage="Êtes-vous sûr de vouloir supprimer {label} ?"
                values={{ label: <b>{label}</b> }}
              />
              : <FormattedMessage
                id="do_you_want_to_delete"
                defaultMessage="Êtes-vous sûr de vouloir supprimer ?"
              />}
            <br />
            <FormattedMessage
              id="this_operation_is_irreversible"
              defaultMessage="Cette opération est irréversible."
            />
          </Text>
          : null}

        {mutation.error instanceof Error
          ? <Alert color="red">
            {mutation.error.message}
          </Alert>
          : null}

        <ButtonGroup>
          <Button
            disabled={mutation.isPending}
            onClick={onClose}
          >
            {mutation.isSuccess
              ? <FormattedMessage
                id="close"
                defaultMessage="Fermer"
              />
              : <FormattedMessage
                id="cancel"
                defaultMessage="Annuler"
              />
            }
          </Button>
          <Button
            variant="filled"
            disabled={mutation.isPending || mutation.isSuccess}
            loading={mutation.isPending}
            onClick={() => {
              mutation.mutate()
            }}
          >
            {mutation.isSuccess
              ? <FormattedMessage
                id="deleted"
                defaultMessage="Supprimé"
              />
              : <FormattedMessage
                id="delete"
                defaultMessage="Supprimer"
              />
            }
          </Button>
        </ButtonGroup>
      </Stack>
    </Modal>
  )
}

export default DeleteModal
